import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  Filter,
  Home,
  LayoutGrid,
  Leaf,
  List,
  PackageCheck,
  ShoppingBag,
  SlidersHorizontal,
  Sparkles,
  Truck,
  X,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import DecorLeaf from '../components/DecorLeaf';
import ErrorState from '../components/ErrorState';
import CatalogProductTile, { CATALOG_GRID as GRID, CatalogTileSkeleton as TileSkeleton } from '../components/CatalogProductTile';
import usePageMetadata from '../hooks/usePageMetadata';
import * as catalogApi from '../api/endpoints/catalog';
import { isStaleLocationError } from '../utils/staleLocation';
import { visibleCategories } from '../utils/categories';
import { useCart } from '../context/CartContext';
import { FAVOURITE_AUTH_REQUIRED, useFavourites } from '../context/FavouritesContext';
import { useToast } from '../context/ToastContext';
import { ICON_BY_HINT, resolveCategoryVisual } from '../config/categoryVisuals';
import { focusRing, homeRail } from '../styles/ui';

const DEFAULT_SORT = 'newest';
const PAGE_LIMIT = 24;

// Only the sorts GET /products actually implements
// (customerAppCatalogController.js).
const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'discount_desc', label: 'Biggest Discount' },
  { value: 'popularity', label: 'Popularity' },
];

// Brand copy already published elsewhere on the site — no new claims.
const HERO_POINTS = [
  { icon: Leaf, title: 'Farm Fresh', subtitle: 'Handpicked daily' },
  { icon: PackageCheck, title: 'Hygienically Packed', subtitle: "For your family's safety" },
  { icon: Sparkles, title: 'Quality Checked', subtitle: 'Assured freshness' },
  { icon: Truck, title: 'Same-Day Delivery', subtitle: 'Freshness at your doorstep' },
];

// The approved design's hero artwork exists for the chicken category; every
// other category shows its own catalogue photo with the same treatment.
const HERO_ART_BY_KEY = { chicken: '/pages/category-hero-chicken.webp' };

const primaryPill = `inline-flex items-center h-11 px-6 rounded-full bg-[linear-gradient(90deg,#2E8B47,#1E7A3C)] text-white text-sm font-bold shadow-[0_8px_18px_-8px_rgba(46,139,71,0.6)] ${focusRing}`;

const EmptyPanel = ({ title, body, action }) => (
  <div className="rounded-[20px] border border-[#E4EDE4] bg-white px-6 py-12 sm:py-14 text-center shadow-[0_8px_22px_-18px_rgba(15,51,24,0.3)]">
    <span className="mx-auto w-14 h-14 rounded-full bg-[#E9F5E6] text-emerald-700 flex items-center justify-center mb-4">
      <ShoppingBag size={24} strokeWidth={1.8} aria-hidden="true" />
    </span>
    <h3 className="serif text-[22px] font-bold text-emerald-950 mb-1.5">{title}</h3>
    <p className="text-[14px] text-ink-secondary mb-5 max-w-md mx-auto">{body}</p>
    {action}
  </div>
);

const FilterSection = ({ title, children, action }) => (
  <div className="border-t border-[#EEF3EE] pt-4 mt-4 first:border-0 first:pt-0 first:mt-0">
    <div className="flex items-center justify-between gap-2 mb-2.5">
      <h3 className="serif text-[17px] font-bold text-emerald-950">{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

const CategoryDetailPage = () => {
  const { categoryKey } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── URL-backed state. Everything here maps to a real GET /products param. ──
  const sort = searchParams.get('sort') || DEFAULT_SORT;
  const subCategory = searchParams.get('subCategory') || '';
  const priceMin = searchParams.get('priceMin') || '';
  const priceMax = searchParams.get('priceMax') || '';

  // ── Presentation / client-side-only state ────────────────────────────────
  const [view, setView] = useState('grid');
  const [availability, setAvailability] = useState('all'); // all | in | out
  const [unitFilter, setUnitFilter] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [priceDraft, setPriceDraft] = useState({ min: priceMin, max: priceMax });

  const [state, setState] = useState({ status: 'loading', data: [], pagination: null, error: null });
  const [loadingMore, setLoadingMore] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const requestIdRef = useRef(0);

  const { addItem, setQuantity, quantityFor } = useCart();
  const { isFavourite, toggleFavourite } = useFavourites();
  const { showToast } = useToast();

  const categoryMeta = categories.find((item) => item.key === categoryKey);
  const categoryLabel = categoryMeta?.label || categoryKey.replace(/-/g, ' ');

  usePageMetadata({
    title: `${categoryLabel} | Fresh & Homely`,
    description: 'Browse products at Fresh & Homely.',
    canonicalPath: `/categories/${categoryKey}`,
  });

  useEffect(() => {
    setPriceDraft({ min: priceMin, max: priceMax });
  }, [priceMin, priceMax]);

  // Categories and their sub-categories are entirely data-driven.
  useEffect(() => {
    let active = true;
    catalogApi
      .listCategories()
      .then((response) => {
        if (!active) return;
        const list = visibleCategories(response.data);
        setCategories(list);
        setSubCategories(list.find((c) => c.key === categoryKey)?.subCategories || []);
      })
      .catch(() => {
        if (active) {
          setCategories([]);
          setSubCategories([]);
        }
      });
    return () => {
      active = false;
    };
  }, [categoryKey]);

  const queryFor = useCallback(
    (page) => ({
      category: categoryKey,
      subCategory: subCategory || undefined,
      sort,
      priceMin: priceMin || undefined,
      priceMax: priceMax || undefined,
      page,
      limit: PAGE_LIMIT,
    }),
    [categoryKey, subCategory, sort, priceMin, priceMax]
  );

  const load = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setState({ status: 'loading', data: [], pagination: null, error: null });
    catalogApi
      .listProducts(queryFor(1))
      .then((response) => {
        if (requestId !== requestIdRef.current) return; // superseded by a newer filter/sort change
        setState({
          status: 'success',
          data: Array.isArray(response.data) ? response.data : [],
          pagination: response.pagination || null,
          error: null,
        });
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current || isStaleLocationError(err)) return;
        setState({ status: 'error', data: [], pagination: null, error: err.message });
      });
  }, [queryFor]);

  useEffect(load, [load]);

  const loadMore = () => {
    const nextPage = (state.pagination?.page || 1) + 1;
    const requestId = requestIdRef.current;
    setLoadingMore(true);
    catalogApi
      .listProducts(queryFor(nextPage))
      .then((response) => {
        if (requestId !== requestIdRef.current) return;
        setState((prev) => ({
          ...prev,
          data: [...prev.data, ...(Array.isArray(response.data) ? response.data : [])],
          pagination: response.pagination || prev.pagination,
        }));
      })
      .catch(() => {
        /* keep what is already loaded */
      })
      .finally(() => setLoadingMore(false));
  };

  // The listing has no Offers filter. An old link or bookmark carrying its
  // `discountMin` parameter is cleaned from the URL (it is never sent to the
  // API), so the page cannot stay silently filtered by it.
  useEffect(() => {
    if (!searchParams.has('discountMin')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('discountMin');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const setParam = (updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    setSearchParams(next);
  };

  const clearFilters = () => {
    // Only this page's own filters — cart, favourites and location are untouched.
    setSearchParams(new URLSearchParams());
    setAvailability('all');
    setUnitFilter('');
    setPriceDraft({ min: '', max: '' });
  };

  const handleToggleFavourite = async (product) => {
    try {
      const wasFavourited = isFavourite(product._id);
      const result = await toggleFavourite(product._id, product);
      if (result === FAVOURITE_AUTH_REQUIRED) {
        window.location.assign(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
        return;
      }
      showToast(wasFavourited ? 'Removed from favourites' : 'Added to favourites');
    } catch {
      // FavouritesContext rolls back its own optimistic update.
    }
  };

  const handleAddToCart = (product) => {
    addItem(product);
    showToast(`${product.displayName} added to cart`);
  };

  // ── Client-side filters ──────────────────────────────────────────────────
  // GET /products has no stock or UOM parameter, so these two narrow the
  // products already loaded. The scope is stated in the UI.
  const unitOptions = useMemo(() => {
    const counts = new Map();
    state.data.forEach((product) => {
      if (!product.sellingUom) return;
      counts.set(product.sellingUom, (counts.get(product.sellingUom) || 0) + 1);
    });
    // The chosen unit stays listed, so it can always be unticked, even when
    // none of the products loaded so far has it.
    if (unitFilter && !counts.has(unitFilter)) counts.set(unitFilter, 0);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [state.data, unitFilter]);

  const stockCounts = useMemo(
    () =>
      state.data.reduce(
        (acc, product) => {
          if (product.isInStock) acc.in += 1;
          else acc.out += 1;
          return acc;
        },
        { in: 0, out: 0 }
      ),
    [state.data]
  );

  const visibleProducts = useMemo(
    () =>
      state.data.filter((product) => {
        if (availability === 'in' && !product.isInStock) return false;
        if (availability === 'out' && product.isInStock) return false;
        if (unitFilter && product.sellingUom !== unitFilter) return false;
        return true;
      }),
    [state.data, availability, unitFilter]
  );

  const serverFiltered = Boolean(subCategory || priceMin || priceMax);
  const clientFiltered = availability !== 'all' || Boolean(unitFilter);
  const anyFilter = serverFiltered || clientFiltered;
  const total = state.pagination?.total;
  // Whether the backend has more pages. Availability and unit only narrow the
  // products loaded so far, so later pages must stay reachable even when none
  // of the loaded ones match.
  const hasMorePages = Boolean(state.pagination?.hasMore);
  const { image: heroImage, Icon: CategoryIcon } = resolveCategoryVisual(categoryKey, categoryMeta?.iconHint);

  const checkbox = 'w-4 h-4 rounded border-[#BEE6B4] accent-[#2E8B47]';

  const filterPanel = (
    <div className="bg-white rounded-2xl border border-[#E6EEE6] shadow-[0_6px_18px_-10px_rgba(15,51,24,0.25)] p-5">
      <FilterSection
        title="Categories"
        action={<ChevronDown size={16} className="text-emerald-800 rotate-180" aria-hidden="true" />}
      >
        <ul className="space-y-0.5">
          {categories.map((item) => {
            const active = item.key === categoryKey;
            const Icon = ICON_BY_HINT[item.iconHint] || ShoppingBag;
            return (
              <li key={item.key}>
                <Link
                  to={`/categories/${item.key}`}
                  aria-current={active ? 'page' : undefined}
                  className={`flex items-center gap-2.5 rounded-lg px-2.5 h-[32px] text-[13px] transition-colors ${focusRing} ${
                    active
                      ? 'bg-[#E4F3E5] text-emerald-900 font-semibold'
                      : 'text-gray-700 hover:bg-emerald-50/60 hover:text-emerald-800'
                  }`}
                >
                  <Icon size={16} className={active ? 'text-emerald-700' : 'text-emerald-800/70'} aria-hidden="true" />
                  <span className="truncate flex-1">{item.label}</span>
                  {/* Counts come from GET /categories; nothing is invented. */}
                  {typeof item.count === 'number' && (
                    <span
                      className={`text-[12px] shrink-0 ${
                        active ? 'inline-flex items-center h-5 px-2 rounded-full bg-[#2E8B47] text-white font-bold' : 'text-ink-secondary'
                      }`}
                    >
                      <span aria-hidden="true">{item.count}</span><span className="sr-only">{` (${item.count})`}</span>
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </FilterSection>

      {subCategories.length > 0 && (
        <FilterSection title="Sub-categories">
          <ul className="space-y-1">
            <li>
              <button
                type="button"
                onClick={() => setParam({ subCategory: '' })}
                aria-pressed={!subCategory}
                className={`w-full text-left rounded-lg px-2.5 h-[32px] text-[13px] transition-colors ${focusRing} ${
                  !subCategory ? 'bg-[#E4F3E5] text-emerald-900 font-semibold' : 'text-gray-700 hover:bg-emerald-50/60'
                }`}
              >
                All
              </button>
            </li>
            {subCategories.map((sub) => (
              <li key={sub.key}>
                <button
                  type="button"
                  onClick={() => setParam({ subCategory: sub.key })}
                  aria-pressed={subCategory === sub.key}
                  className={`w-full text-left rounded-lg px-2.5 h-[32px] text-[13px] transition-colors ${focusRing} ${
                    subCategory === sub.key
                      ? 'bg-[#E4F3E5] text-emerald-900 font-semibold'
                      : 'text-gray-700 hover:bg-emerald-50/60'
                  }`}
                >
                  {sub.label}
                </button>
              </li>
            ))}
          </ul>
        </FilterSection>
      )}

      {/* Server-side: the API's own priceMin/priceMax. No invented ceiling. */}
      <FilterSection title="Price Range">
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="0"
            inputMode="numeric"
            value={priceDraft.min}
            onChange={(event) => setPriceDraft((prev) => ({ ...prev, min: event.target.value }))}
            aria-label="Minimum price"
            placeholder="₹ Min"
            className={`w-full h-9 px-2.5 rounded-lg border border-[#DCE8DC] text-sm ${focusRing}`}
          />
          <span className="text-ink-secondary">–</span>
          <input
            type="number"
            min="0"
            inputMode="numeric"
            value={priceDraft.max}
            onChange={(event) => setPriceDraft((prev) => ({ ...prev, max: event.target.value }))}
            aria-label="Maximum price"
            placeholder="₹ Max"
            className={`w-full h-9 px-2.5 rounded-lg border border-[#DCE8DC] text-sm ${focusRing}`}
          />
        </div>
        <button
          type="button"
          onClick={() => setParam({ priceMin: priceDraft.min, priceMax: priceDraft.max })}
          className={`mt-2 w-full h-[36px] rounded-lg bg-[linear-gradient(90deg,#2E8B47,#1E7A3C)] text-white text-[13px] font-bold inline-flex items-center justify-center gap-2 hover:brightness-105 ${focusRing}`}
        >
          <SlidersHorizontal size={14} aria-hidden="true" />
          Apply price
        </button>
      </FilterSection>

      {/* Client-side: GET /products has no stock parameter. */}
      <FilterSection title="Availability">
        <ul className="space-y-1.5">
          {[
            { id: 'in', label: 'In Stock', count: stockCounts.in },
            { id: 'out', label: 'Out of Stock', count: stockCounts.out },
          ].map((option) => (
            <li key={option.id}>
              <label className="flex items-center justify-between gap-2 text-[13px] text-gray-700 cursor-pointer">
                <span className="flex items-center gap-2.5">
                  <input
                    type="checkbox"
                    checked={availability === option.id}
                    onChange={() => setAvailability(availability === option.id ? 'all' : option.id)}
                    className={checkbox}
                  />
                  {option.label}
                </span>
                <span className="text-xs text-ink-secondary">({option.count})</span>
              </label>
            </li>
          ))}
        </ul>
      </FilterSection>

      {/* Client-side: no UOM parameter exists either. Options come from the
          loaded products, never a fixed list. */}
      {unitOptions.length > 0 && (
        <FilterSection title="Unit Type">
          <ul className="space-y-1.5">
            {unitOptions.map(([unit, count]) => (
              <li key={unit}>
                <label className="flex items-center justify-between gap-2 text-[13px] text-gray-700 cursor-pointer">
                  <span className="flex items-center gap-2.5">
                    <input
                      type="checkbox"
                      checked={unitFilter === unit}
                      onChange={() => setUnitFilter(unitFilter === unit ? '' : unit)}
                      className={checkbox}
                    />
                    {unit}
                  </span>
                  <span className="text-xs text-ink-secondary">({count})</span>
                </label>
              </li>
            ))}
          </ul>
        </FilterSection>
      )}

      {clientFiltered && (
        <p className="text-[11px] text-ink-secondary mt-3">
          Availability and unit filters apply to the {state.data.length} products loaded here.
        </p>
      )}

      <button
        type="button"
        onClick={clearFilters}
        className={`mt-4 w-full h-10 rounded-xl border border-[#2E8B47] text-sm font-bold text-[#1E5A30] hover:bg-emerald-50 transition-colors ${focusRing}`}
      >
        Clear Filters
      </button>
    </div>
  );

  return (
    <>
      <Navbar />

      <main>
        {/* ── Category hero ──────────────────────────────────────────────── */}
        <section className="relative overflow-hidden pt-20 bg-[linear-gradient(180deg,#F3FAF1_0%,#F7FBF5_70%,#FFFFFF_100%)]">
          <div className="pointer-events-none select-none absolute inset-x-0 top-20 bottom-0" aria-hidden="true">
            {HERO_ART_BY_KEY[categoryKey] ? (
              <img
                src={HERO_ART_BY_KEY[categoryKey]}
                alt=""
                width="801"
                height="238"
                className="hidden lg:block absolute right-0 top-0 h-full w-auto"
              />
            ) : (
              heroImage && (
                <img
                  src={heroImage}
                  alt=""
                  className="hidden lg:block absolute right-0 top-0 h-full w-[46%] object-cover"
                  style={{
                    WebkitMaskImage: 'linear-gradient(to right, transparent 0%, #000 30%)',
                    maskImage: 'linear-gradient(to right, transparent 0%, #000 30%)',
                  }}
                />
              )
            )}
            <DecorLeaf className="hidden xl:block absolute left-[20px] top-[30px] w-10 text-emerald-500/70 -rotate-[24deg]" />
            <DecorLeaf className="hidden xl:block absolute left-[620px] top-[70px] w-8 text-emerald-500/70 rotate-[20deg]" />
            <DecorLeaf className="hidden xl:block absolute left-[640px] top-[130px] w-6 text-emerald-600/60 -rotate-[10deg]" />
          </div>

          <div className={`${homeRail} relative`}>
            <div className="pt-[24px] pb-[20px] xl:min-h-[237px] xl:pl-[40px]">
              <nav aria-label="Breadcrumb">
                <ol className="flex items-center gap-1.5 text-[12.5px] text-ink-secondary">
                  <li>
                    <Link to="/" className={`inline-flex items-center gap-1.5 hover:text-emerald-700 ${focusRing}`}>
                      <Home size={13} aria-hidden="true" />
                      Home
                    </Link>
                  </li>
                  <li aria-hidden="true">
                    <ChevronRight size={13} />
                  </li>
                  <li>
                    <Link to="/categories" className={`hover:text-emerald-700 ${focusRing}`}>
                      Categories
                    </Link>
                  </li>
                  <li aria-hidden="true">
                    <ChevronRight size={13} />
                  </li>
                  <li aria-current="page" className="font-medium text-emerald-800">
                    {categoryLabel}
                  </li>
                </ol>
              </nav>

              <div className="flex items-center gap-4 mt-[14px]">
                <span
                  className="w-[54px] h-[54px] rounded-full bg-white/80 text-[#E37B22] flex items-center justify-center shrink-0 shadow-[0_2px_8px_-2px_rgba(15,51,24,0.15)]"
                  aria-hidden="true"
                >
                  <CategoryIcon className="w-8 h-8" strokeWidth={1.8} />
                </span>
                <h1 className="serif text-[36px] sm:text-[42px] xl:text-[46px] font-bold text-emerald-950 leading-[1.05] capitalize">
                  {categoryLabel}
                </h1>
              </div>
              <p className="mt-[8px] max-w-[420px] text-[14px] leading-[20px] text-[#4B5563] xl:pl-[70px]">
                Fresh, hygienically packed and delivered to your door.
              </p>

              <ul className="flex flex-wrap gap-x-2 gap-y-3 mt-[18px] xl:flex-nowrap xl:divide-x xl:divide-[#DCE8DC] xl:max-w-[760px]">
                {HERO_POINTS.map(({ icon: Icon, title, subtitle }) => (
                  <li key={title} className="flex items-center gap-2 xl:pl-2.5 first:xl:pl-0 xl:pr-1 whitespace-nowrap">
                    <span className="w-[42px] h-[42px] rounded-full bg-white text-emerald-700 border border-[#E6EEE6] flex items-center justify-center shrink-0 shadow-[0_2px_6px_-2px_rgba(15,51,24,0.12)]">
                      <Icon size={19} strokeWidth={1.9} aria-hidden="true" />
                    </span>
                    <span>
                      <span className="block text-[12.5px] font-bold text-emerald-950 leading-tight">{title}</span>
                      <span className="block text-[11px] text-ink-secondary leading-tight mt-[3px]">{subtitle}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ── Catalogue ──────────────────────────────────────────────────── */}
        <section className="pt-6 pb-12 bg-[linear-gradient(180deg,#FFFFFF_0%,#F6FAF4_220px)]">
          <div className={homeRail}>
            <div className="grid grid-cols-1 lg:grid-cols-[263px_minmax(0,1fr)] gap-8 items-start xl:px-[9px]">
              <div className="hidden lg:block lg:sticky lg:top-24">{filterPanel}</div>

              <div className="min-w-0">
                {/* Results header */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-[18px]">
                  <div>
                    <h2 className="serif text-[22px] font-bold text-emerald-950 capitalize leading-tight">{categoryLabel}</h2>
                    {state.status === 'success' && (
                      <p className="text-[13.5px] text-ink-secondary mt-[3px]">
                        {typeof total === 'number'
                          ? `Showing ${visibleProducts.length} of ${total} product${total === 1 ? '' : 's'}`
                          : `Showing ${visibleProducts.length} product${visibleProducts.length === 1 ? '' : 's'}`}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2.5">
                    <button
                      type="button"
                      onClick={() => setFiltersOpen(true)}
                      className={`lg:hidden inline-flex items-center gap-1.5 h-10 px-4 rounded-xl border border-emerald-200 text-sm font-semibold text-emerald-800 ${focusRing}`}
                    >
                      <Filter size={16} aria-hidden="true" />
                      Filters
                    </button>

                    <label htmlFor="sort" className="hidden sm:block text-[13px] text-ink-secondary">
                      Sort by:
                    </label>
                    <select
                      id="sort"
                      value={sort}
                      onChange={(event) => setParam({ sort: event.target.value === DEFAULT_SORT ? '' : event.target.value })}
                      aria-label="Sort products"
                      className={`h-[38px] px-3 pr-8 rounded-lg border border-[#DCE8DC] bg-white text-[13px] font-semibold text-emerald-950 ${focusRing}`}
                    >
                      {SORT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>

                    <div className="hidden sm:flex items-center gap-1.5">
                      {[
                        { id: 'grid', icon: LayoutGrid, label: 'Grid view' },
                        { id: 'list', icon: List, label: 'List view' },
                      ].map(({ id, icon: Icon, label }) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setView(id)}
                          aria-label={label}
                          aria-pressed={view === id}
                          className={`w-[38px] h-[38px] rounded-lg border flex items-center justify-center transition-colors ${focusRing} ${
                            view === id
                              ? 'bg-[#2E8B47] border-[#2E8B47] text-white'
                              : 'bg-white border-[#DCE8DC] text-emerald-800 hover:bg-emerald-50'
                          }`}
                        >
                          <Icon size={17} aria-hidden="true" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {state.status === 'loading' && (
                  <ul className={GRID}>
                    {Array.from({ length: 6 }).map((_, index) => (
                      <TileSkeleton key={index} />
                    ))}
                  </ul>
                )}

                {state.status === 'error' && <ErrorState message={state.error} onRetry={load} />}

                {state.status === 'success' && state.data.length === 0 && (
                  <EmptyPanel
                    title={anyFilter ? 'No products match these filters' : 'No products available in this category right now.'}
                    body={anyFilter ? 'Try widening your price range or clearing a filter.' : 'Check back soon, or explore our other fresh categories.'}
                    action={
                      anyFilter ? (
                        <button type="button" onClick={clearFilters} className={primaryPill}>
                          Clear filters
                        </button>
                      ) : (
                        <Link to="/categories" className={primaryPill}>
                          Browse categories
                        </Link>
                      )
                    }
                  />
                )}

                {state.status === 'success' && state.data.length > 0 && visibleProducts.length === 0 && (
                  <EmptyPanel
                    title={hasMorePages ? 'No matching products in the products loaded so far' : 'No products match these filters'}
                    body={
                      hasMorePages
                        ? 'This category has more products. Load more to keep looking, or change a filter.'
                        : 'Try a different availability or unit selection.'
                    }
                    action={
                      <button type="button" onClick={clearFilters} className={primaryPill}>
                        Clear filters
                      </button>
                    }
                  />
                )}

                {state.status === 'success' && visibleProducts.length > 0 && (
                  <ul
                    data-testid="product-grid"
                    className={
                      view === 'grid'
                        ? GRID
                        : 'flex flex-col gap-3.5'
                    }
                  >
                    {visibleProducts.map((product) => (
                      <CatalogProductTile
                        key={product._id}
                        product={product}
                        view={view}
                        quantity={quantityFor(product._id)}
                        favourited={isFavourite(product._id)}
                        onAddToCart={() => handleAddToCart(product)}
                        onSetQuantity={setQuantity}
                        onToggleFavourite={() => handleToggleFavourite(product)}
                      />
                    ))}
                  </ul>
                )}

                {/* Server pagination is preserved — more pages are appended.
                    Offered whenever the backend has more, whatever the
                    client-side filters leave visible. */}
                {state.status === 'success' && hasMorePages && (
                  <div className="flex justify-center mt-7">
                    <button
                      type="button"
                      onClick={loadMore}
                      disabled={loadingMore}
                      className={`inline-flex items-center h-11 px-7 rounded-full border border-emerald-200 text-sm font-semibold text-emerald-800 hover:bg-emerald-50 transition-colors disabled:opacity-60 ${focusRing}`}
                    >
                      {loadingMore ? 'Loading…' : 'Load more products'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

      </main>

      {/* Mobile filter drawer */}
      {filtersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-emerald-950/40"
            onClick={() => setFiltersOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute inset-y-0 left-0 w-[88%] max-w-sm bg-canvas overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-emerald-950">Filters</h2>
              <button
                type="button"
                onClick={() => setFiltersOpen(false)}
                aria-label="Close filters"
                className={`w-10 h-10 rounded-full bg-white border border-emerald-100 flex items-center justify-center ${focusRing}`}
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>
            {filterPanel}
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default CategoryDetailPage;
